import { EDITOR_VERSION } from "@wiki/core";

import { colorForUser } from "../editor/collab";
import type { AwarenessLike, CollaborationUser } from "../editor/types";

/**
 * Everything about a collaboration session that is a pure function of its inputs, kept apart
 * from React and from `@hocuspocus/provider` so it can be tested directly.
 */

/** What the UI shows. `readOnly` and `reloadRequired` are states, not errors. */
export type CollaborationStatus =
  "connecting" | "connected" | "disconnected" | "readOnly" | "reloadRequired";

/** The close codes of `docs/spec/collaboration.md` § Errors that the client branches on. */
export const CLOSE_CODES = {
  documentTooLarge: 1009,
  authorizationChanged: 4205,
  authenticationFailed: 4401,
  authorizationFailed: 4403,
  connectionTimeout: 4408,
  tooManyConnections: 4503,
  editorUpdateRequired: 4999,
} as const;

/**
 * Reconnect pacing (§15).
 *
 * The socket library backs off exponentially only while a connection fails to *open*. Our
 * refusals are not that: the server accepts the upgrade and then closes with a code, so the
 * socket does open, the library treats the attempt as a success, and its next retry is the flat
 * base delay — a refused client would hammer the endpoint once a second for as long as the
 * refusal lasts. So the base delay is what we steer instead, per close code, and a connection
 * that actually carries traffic puts it back.
 */
export const RECONNECT_DELAY_MS = 1_000;
/** `4205` says access merely *may* have changed, so the client retries at once (§15). */
export const IMMEDIATE_RETRY_MS = 0;
/** A refusal that will very likely refuse again: wait, but stay responsive to a real fix. */
export const REFUSED_RETRY_DELAY_MS = 5_000;
/** A rate refusal is a request to stop asking; the window it protects is a minute long. */
export const THROTTLED_RETRY_DELAY_MS = 30_000;

/**
 * `4503` covers both the per-document cap and the connection-rate throttle, so the reason
 * string is what separates them (`docs/spec/collaboration.md` §4). It is part of the wire
 * contract for exactly this purpose.
 */
export const RATE_LIMIT_CLOSE_REASON = "Connection Rate Limited";

export interface CloseEventLike {
  code: number;
  reason?: string;
}

/** Whether this close is the connection-rate throttle rather than the per-document cap. */
export function isRateLimitedClose(close: CloseEventLike): boolean {
  return close.code === CLOSE_CODES.tooManyConnections && close.reason === RATE_LIMIT_CLOSE_REASON;
}

/**
 * How long to wait before the next attempt after this close. Anything that is not an admission
 * refusal — a normal close, a dropped network, an idle timeout — keeps the ordinary delay,
 * because reconnecting promptly is the whole point of a live session.
 */
export function reconnectDelayMs(close: CloseEventLike): number {
  if (isRateLimitedClose(close)) {
    return THROTTLED_RETRY_DELAY_MS;
  }
  switch (close.code) {
    case CLOSE_CODES.authorizationChanged:
      return IMMEDIATE_RETRY_MS;
    case CLOSE_CODES.authenticationFailed:
    case CLOSE_CODES.authorizationFailed:
    case CLOSE_CODES.tooManyConnections:
      return REFUSED_RETRY_DELAY_MS;
    default:
      return RECONNECT_DELAY_MS;
  }
}

export interface SessionState {
  /** What the socket itself reports. */
  socket: "connecting" | "connected" | "disconnected";
  /** The scope the server granted on the last successful authentication. */
  scope: "read-write" | "readonly" | null;
  /** Set once the server has refused this editor version; never cleared by a reconnect. */
  reloadRequired: boolean;
  /** The last close code seen, for the banner's message. */
  lastCloseCode: number | null;
}

export const initialSessionState: SessionState = {
  socket: "connecting",
  scope: null,
  reloadRequired: false,
  lastCloseCode: null,
};

/**
 * `4999` is terminal: §15 says the client stops accepting keystrokes and stops reconnecting,
 * because retrying would loop forever against a server that will never accept it. Every other
 * code leaves the session reconnectable, so the status follows the socket.
 */
export function statusOf(state: SessionState): CollaborationStatus {
  if (state.reloadRequired) {
    return "reloadRequired";
  }
  if (state.socket !== "connected") {
    return state.socket === "connecting" ? "connecting" : "disconnected";
  }
  if (state.scope === "readonly") {
    return "readOnly";
  }
  return state.scope === null ? "connecting" : "connected";
}

/** Whether the editing surface accepts input in this state (§11, §15). */
export function isEditable(status: CollaborationStatus, hasLocalPersistence: boolean): boolean {
  if (status === "readOnly" || status === "reloadRequired") {
    return false;
  }
  // Offline edits are only safe when they can be stored; without IndexedDB a refresh would
  // lose them, so the client is conservative and blocks editing instead (§15).
  return status === "connected" || hasLocalPersistence;
}

export interface CollaborationPeer extends CollaborationUser {
  clientId: number;
  /** Scroll offset in viewport heights, the unit §10 broadcasts it in. */
  scrollY: number | null;
  isSelf: boolean;
}

interface AwarenessUser {
  id?: unknown;
  name?: unknown;
  color?: unknown;
}

/**
 * Projects raw awareness states into the presence row. One person with three tabs is three
 * connections but one avatar (§ edge case 6), so entries are de-duplicated by user id with
 * the most recent state winning.
 */
export function peersFrom(
  states: ReadonlyMap<number, Record<string, unknown>>,
  selfClientId: number | null,
): CollaborationPeer[] {
  const byUser = new Map<string, CollaborationPeer>();
  for (const [clientId, state] of states) {
    const user = state.user as AwarenessUser | undefined;
    if (typeof user?.id !== "string" || user.id === "") {
      continue;
    }
    const name = typeof user.name === "string" && user.name !== "" ? user.name : "Unknown";
    const color =
      typeof user.color === "string" && user.color !== "" ? user.color : colorForUser(user.id);
    const scrollY = typeof state.scrollY === "number" ? state.scrollY : null;
    const isSelf = clientId === selfClientId;
    const existing = byUser.get(user.id);
    byUser.set(user.id, {
      clientId: existing?.isSelf === true ? existing.clientId : clientId,
      id: user.id,
      name,
      color,
      scrollY: scrollY ?? existing?.scrollY ?? null,
      isSelf: isSelf || existing?.isSelf === true,
    });
  }
  return [...byUser.values()].sort((left, right) => left.name.localeCompare(right.name));
}

/** The URL a client opens, per §1: the document name in the path, token and version in query. */
export function collaborationUrl(options: {
  documentId: string;
  token: string;
  origin?: string;
  editorVersion?: string;
}): string {
  const origin = options.origin ?? (typeof window === "undefined" ? "" : window.location.origin);
  const base = origin.replace(/^http/, "ws").replace(/\/+$/, "");
  const query = new URLSearchParams({
    token: options.token,
    editorVersion: options.editorVersion ?? EDITOR_VERSION,
  });
  return `${base}/collaboration/document.${options.documentId}?${query.toString()}`;
}

/** The IndexedDB mirror shares the document's wire name so both ends agree on identity (§15). */
export function localStoreName(documentId: string): string {
  return `document.${documentId}`;
}

/**
 * §10: cursor updates are not throttled — they are small and latency matters — but `scrollY`
 * is capped at four per second. Trailing-edge so the final position is always published.
 */
export interface Throttled<T> {
  (value: T): void;
  cancel: () => void;
}

export function throttle<T>(publish: (value: T) => void, intervalMs: number): Throttled<T> {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { value: T } | null = null;

  const send = (value: T): void => {
    last = Date.now();
    publish(value);
  };
  const throttled = (value: T): void => {
    const elapsed = Date.now() - last;
    if (elapsed >= intervalMs) {
      send(value);
      return;
    }
    pending = { value };
    timer ??= setTimeout(() => {
      timer = null;
      if (pending) {
        const next = pending.value;
        pending = null;
        send(next);
      }
    }, intervalMs - elapsed);
  };
  throttled.cancel = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    pending = null;
  };
  return throttled;
}

export const SCROLL_THROTTLE_MS = 250;

export type { AwarenessLike, CollaborationUser };
